import "@ainauten/tokens/css";
import "@ainauten/ui/shell.css";
import "./styles.css";
import { createRoot } from "react-dom/client";
import { BRAND_TEMPLATES, TEMPLATE_SOURCE_COMMIT, TEMPLATE_SOURCE_REPO, type BrandTemplate } from "./brandTemplates";
import { synchronizeDesignDocument } from "./designDocument";
import { ShellFooter, ShellHeader } from "./shell";

interface VisualAsset {
  url: string;
  type: "favicon" | "og-image" | "image";
  label?: string;
  source?: string;
  confidence?: Confidence;
}

type Confidence = "high" | "medium" | "low";
type ColorRole = "primary" | "secondary" | "accent" | "background" | "surface" | "border" | "on-accent";

interface EvidenceItem {
  value: string;
  source: string;
  confidence: Confidence;
  count?: number;
  score?: number;
  suggestedRole?: ColorRole;
}

interface EditableColor {
  manual?: boolean;
  role: ColorRole;
  value: string;
  evidence?: EvidenceItem;
}

interface GenerateResponse {
  designMd: string;
  designMdDe?: string;
  meta: {
    domain: string;
    brandName: string;
    sourceUrls: string[];
    colors: string[];
    colorEvidence: EvidenceItem[];
    fonts: string[];
    fontEvidence: EvidenceItem[];
    logos: VisualAsset[];
    visualAssets: VisualAsset[];
    warnings: string[];
    lintSummary: {
      errors: number;
      warnings: number;
      info: number;
    };
  };
}

type ResultLanguage = "en" | "de";

interface ConfigResponse {
  turnstileSiteKey: string;
}

declare global {
  interface Window {
    turnstile?: {
      render: (element: string | HTMLElement, options: { sitekey: string; callback: (token: string) => void }) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

const MAX_URLS = 3;
const COLOR_TOKEN_NAMES: ColorRole[] = ["primary", "secondary", "accent", "background", "surface", "border", "on-accent"];
const COLOR_TOKEN_LABELS: Record<string, string> = {
  primary: "Primary",
  secondary: "Secondary",
  accent: "Accent",
  background: "Background",
  surface: "Surface",
  border: "Border",
  "on-accent": "On accent",
};
const FALLBACK_COLORS: Record<string, string> = {
  primary: "#111111",
  secondary: "#6B7280",
  accent: "#7B2FF7",
  background: "#FFFFFF",
  surface: "#FFFFFF",
  border: "#E5E5E5",
  "on-accent": "#FFFFFF",
};
const LANGUAGE_LABELS: Record<ResultLanguage, string> = {
  en: "EN",
  de: "DE",
};

const app = document.querySelector<HTMLDivElement>("#app");

if (!app) {
  throw new Error("App root not found.");
}

app.innerHTML = `
  <div id="sharedHeader"></div>

  <main class="workspace">
    <section class="intro-strip">
      <div>
        <p class="eyebrow">DESIGN.md Generator</p>
        <h1>Erstelle ein AI-Design-Briefing aus deiner Webseite in 1 Minute.</h1>
      </div>
      <p>
        Damit ChatGPT, Claude, Codex & Co. dein Design richtig verstehen:
        Sammle Farben, Schriften, Logos und Bildsignale als klare visuelle Leitplanke.
      </p>
    </section>

    <section class="tool-stack">
      <details class="panel source-panel" open>
        <summary class="panel__head source-summary">
          <div>
            <p class="panel__kicker">Quellen</p>
            <h2>Websites analysieren</h2>
          </div>
          <span class="source-summary__actions">
            <span id="statusPill" class="status-pill" role="status" aria-live="polite">Bereit</span>
            <span class="source-chevron" aria-hidden="true">
              <svg viewBox="0 0 20 20" fill="none" focusable="false">
                <path d="M5.5 12.2 10 7.8l4.5 4.4" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </span>
          </span>
        </summary>

        <div class="source-panel__body">
          <form id="generatorForm" class="form">
            <div class="sources-head">
              <label class="input-label" for="urlInput-0">URLs</label>
              <button id="addUrlButton" class="button button--soft button--icon" type="button" aria-label="Weitere URL hinzufügen" title="Weitere URL hinzufügen">+</button>
            </div>
            <div id="sourceList" class="source-list"></div>
            <div id="turnstileSlot" class="turnstile-slot" hidden></div>
            <p class="helper-text">
              Nutze das +-Symbol, wenn du verschiedene Quellen-URLs des gleichen Brands analysieren willst.
            </p>
            <button id="generateButton" class="button button--primary" type="submit">DESIGN.md generieren</button>
            <details class="template-panel">
              <summary>
                <span>Brand-Vorlagen</span>
                <span class="template-source-help" title="Quelle: ältere Version von VoltAgent/awesome-design-md (${TEMPLATE_SOURCE_COMMIT.slice(0, 7)}). Die Vorlagen sind kuratierte Inspirationen, keine offiziellen Brand Manuals.">?</span>
              </summary>
              <div class="template-panel__copy">
                Lade eines von ${BRAND_TEMPLATES.length} DESIGN.md-Beispielen bekannter Brands als Ausgangspunkt und passe es unten an.
              </div>
              <div id="templateRail" class="template-rail" aria-label="Brand-Vorlagen"></div>
            </details>
          </form>
        </div>
      </details>

      <div class="panel panel--evidence">
        <div class="panel__head">
          <div>
            <p class="panel__kicker">Evidenz</p>
            <h2>Prüfen & anpassen</h2>
          </div>
        </div>
        <div class="evidence-list">
          <div>
            <label class="field-label" for="brandEditor">Brand</label>
            <input id="brandEditor" class="input input--compact" type="text" value="Noch nicht analysiert" disabled />
          </div>
          <div>
            <p class="evidence-term">Domain</p>
            <div id="domainValue" class="evidence-value">-</div>
          </div>
          <div>
            <div class="editor-head">
              <p class="evidence-term">Farben</p>
              <button id="addColorButton" class="mini-button" type="button" disabled>+ Farbe</button>
            </div>
            <div id="colorEditor" class="token-editor evidence-value">-</div>
          </div>
          <div>
            <div class="editor-head">
              <p class="evidence-term">Fonts</p>
              <button id="addFontButton" class="mini-button" type="button" disabled>+ Font</button>
            </div>
            <div id="fontEditor" class="token-editor evidence-value">-</div>
          </div>
          <div>
            <div class="editor-head">
              <p class="evidence-term">Logos & Visuals</p>
              <button id="downloadImagesButton" class="mini-button" type="button" disabled>ZIP</button>
            </div>
            <div id="visualValue" class="visual-grid evidence-value">Noch keine Visuals erkannt</div>
          </div>
          <div>
            <p class="evidence-term">Lint</p>
            <div id="lintValue" class="evidence-value">-</div>
          </div>
        </div>
        <div id="warningsBox" class="warnings" role="alert" hidden></div>
      </div>
    </section>

    <section class="panel result-panel">
      <div class="panel__head">
        <div>
          <p class="panel__kicker">Output</p>
          <h2>DESIGN.md</h2>
        </div>
        <div class="action-row">
          <div id="languageTabs" class="language-tabs" hidden>
            <button class="language-tab is-active" type="button" data-language="en">EN</button>
            <button class="language-tab" type="button" data-language="de">DE</button>
          </div>
          <button id="copyButton" class="button button--soft" type="button" disabled>Kopieren</button>
          <button id="downloadButton" class="button button--soft" type="button" disabled>Download .md</button>
        </div>
      </div>
      <textarea id="outputText" class="output" spellcheck="false" placeholder="Nach der Generierung erscheint hier die DESIGN.md-Datei. Du kannst sie direkt bearbeiten oder oben die erkannten Werte anpassen."></textarea>
    </section>

    <section id="faq" class="faq-section">
      <div>
        <p class="panel__kicker">FAQ</p>
        <h2>Häufige Fragen</h2>
      </div>
      <div class="faq-grid">
        <details open>
          <summary>Was ist eine DESIGN.md?</summary>
          <p><code>DESIGN.md</code> ist eine kompakte Design-System-Datei für Coding-Agenten. Sie beschreibt Farben, Typografie, Abstände, Radien, Komponenten und Do's/Don'ts so, dass Codex, Claude oder andere Agenten beim UI-Bauen konsistenter entscheiden.</p>
        </details>
        <details>
          <summary>Warum mehrere URLs?</summary>
          <p>Viele Marken wirken je Kanal etwas anders: Hauptwebsite, Landing Page und Newsletter verwenden oft andere Akzente, CTA-Farben oder Bildstile. Mehrere Quellen ergeben eine robustere Design-Leitplanke.</p>
        </details>
        <details>
          <summary>Wie verwende ich die Datei im Agenten-Chat?</summary>
          <p>Kopiere die Datei in den Chat oder lege sie im Projekt als <code>DESIGN.md</code> ab. Danach gibst du dem Agenten die Anweisung, diese Datei als visuelle Leitplanke für Layout, Komponenten und Styling zu nutzen. Typische Font-Entscheidung: <code>Inter</code> für ruhige SaaS- und Tool-Oberflächen, <code>Geist</code> für klare Developer-Tools und technische Dashboards.</p>
        </details>
        <details>
          <summary>Werden Logos und Visuals übernommen?</summary>
          <p>Das Tool erkennt Favicons, Open-Graph-Bilder und sichtbare Bildpfade. Die Links dienen als Evidenz für den Agenten; geschützte Assets werden nicht gespeichert oder kopiert.</p>
        </details>
        <details>
          <summary>Kann ich falsche Farben korrigieren?</summary>
          <p>Ja. Du kannst erkannte Farben und Fonts löschen, ändern oder ergänzen. Die Vorschau und der Download werden direkt aktualisiert, bevor du die Datei weitergibst.</p>
        </details>
        <details>
          <summary>Ist das ein offizielles Brand Manual?</summary>
          <p>Nein. Die Datei ist eine beobachtete Design-Leitplanke aus öffentlich sichtbaren Signalen. Sie hilft beim konsistenten Umsetzen, ersetzt aber keine offiziellen Brand Guidelines oder rechtliche Freigaben.</p>
        </details>
        <details>
          <summary>Woher kommen die Brand-Vorlagen?</summary>
          <p>Die Galerie nutzt kuratierte DESIGN.md-Beispiele aus einer älteren Version von <a href="${TEMPLATE_SOURCE_REPO}" target="_blank" rel="noreferrer">VoltAgent/awesome-design-md</a> (Commit <code>${TEMPLATE_SOURCE_COMMIT.slice(0, 7)}</code>). Sie sind Inspirationen und Startpunkte, keine offiziellen Brand Manuals der jeweiligen Firmen.</p>
        </details>
      </div>
    </section>
  </main>

  <div id="sharedFooter"></div>
`;

createRoot(query<HTMLDivElement>("#sharedHeader")).render(ShellHeader());
createRoot(query<HTMLDivElement>("#sharedFooter")).render(ShellFooter());

const form = query<HTMLFormElement>("#generatorForm");
const sourceList = query<HTMLDivElement>("#sourceList");
const addUrlButton = query<HTMLButtonElement>("#addUrlButton");
const generateButton = query<HTMLButtonElement>("#generateButton");
const statusPill = query<HTMLSpanElement>("#statusPill");
const outputText = query<HTMLTextAreaElement>("#outputText");
const copyButton = query<HTMLButtonElement>("#copyButton");
const downloadButton = query<HTMLButtonElement>("#downloadButton");
const languageTabs = query<HTMLDivElement>("#languageTabs");
const templateRail = query<HTMLDivElement>("#templateRail");
const brandEditor = query<HTMLInputElement>("#brandEditor");
const domainValue = query<HTMLElement>("#domainValue");
const colorEditor = query<HTMLElement>("#colorEditor");
const fontEditor = query<HTMLElement>("#fontEditor");
const visualValue = query<HTMLElement>("#visualValue");
const addColorButton = query<HTMLButtonElement>("#addColorButton");
const addFontButton = query<HTMLButtonElement>("#addFontButton");
const downloadImagesButton = query<HTMLButtonElement>("#downloadImagesButton");
const lintValue = query<HTMLElement>("#lintValue");
const warningsBox = query<HTMLDivElement>("#warningsBox");
const turnstileSlot = query<HTMLDivElement>("#turnstileSlot");

let currentDesignMd = "";
let baseDesignMds: Partial<Record<ResultLanguage, string>> = {};
let currentDesignMds: Partial<Record<ResultLanguage, string>> = {};
let activeLanguage: ResultLanguage = "en";
let availableLanguages: ResultLanguage[] = ["en"];
let resultMode: "generated" | "template" = "generated";
let currentDomain = "design";
let editableColors: EditableColor[] = [];
let editableFonts: string[] = [];
let manualFonts = new Set<number>();
let editableFontEvidence: EvidenceItem[] = [];
let currentVisualAssets: VisualAsset[] = [];
let selectedVisualUrls = new Set<string>();
let turnstileToken = "";
let turnstileWidgetId = "";
let sourceRows = [0];
let nextSourceId = 1;
let activeTemplateSlug = "";

initializeTheme();
renderSourceRows();
renderTemplateRail();
renderLanguageTabs();
loadConfig();

form.addEventListener("submit", async (event) => {
  event.preventDefault();
  await generate();
});

sourceList.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const removeButton = target.closest<HTMLButtonElement>("[data-remove-source]");
  if (!removeButton) {
    return;
  }
  sourceRows = sourceRows.filter((rowId) => rowId !== Number(removeButton.dataset.removeSource));
  renderSourceRows(getSourceValues());
});

addUrlButton.addEventListener("click", () => {
  if (sourceRows.length >= MAX_URLS) {
    return;
  }
  const values = getSourceValues();
  sourceRows.push(nextSourceId);
  nextSourceId += 1;
  renderSourceRows(values);
});

brandEditor.addEventListener("input", refreshManualDesignMd);
outputText.addEventListener("input", () => {
  currentDesignMd = outputText.value;
  currentDesignMds[activeLanguage] = outputText.value;
});

languageTabs.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const button = target.closest<HTMLButtonElement>("[data-language]");
  if (!button) {
    return;
  }
  const language = button.dataset.language as ResultLanguage;
  if (!availableLanguages.includes(language)) {
    return;
  }
  setActiveLanguage(language);
});

templateRail.addEventListener("click", (event) => {
  const target = event.target as HTMLElement;
  const button = target.closest<HTMLButtonElement>("[data-template-slug]");
  if (!button) {
    return;
  }
  const template = BRAND_TEMPLATES.find((item) => item.slug === button.dataset.templateSlug);
  if (template) {
    void loadBrandTemplate(template);
  }
});

addColorButton.addEventListener("click", () => {
  editableColors.push({ role: nextAvailableColorRole(), value: "#7B2FF7", manual: true });
  renderColorEditor();
  refreshManualDesignMd();
});

addFontButton.addEventListener("click", () => {
  manualFonts.add(editableFonts.length);
  editableFonts.push("Inter");
  renderFontEditor();
  refreshManualDesignMd();
});

colorEditor.addEventListener("input", handleTokenInput);
colorEditor.addEventListener("change", handleTokenInput);
colorEditor.addEventListener("click", handleTokenRemove);
fontEditor.addEventListener("input", handleTokenInput);
fontEditor.addEventListener("click", handleTokenRemove);
visualValue.addEventListener("click", handleVisualSelection);

copyButton.addEventListener("click", async () => {
  if (!currentDesignMd) {
    return;
  }
  await navigator.clipboard.writeText(currentDesignMd);
  copyButton.textContent = "Kopiert";
  window.setTimeout(() => {
    copyButton.textContent = "Kopieren";
  }, 1400);
});

downloadButton.addEventListener("click", () => {
  if (!currentDesignMd) {
    return;
  }
  const blob = new Blob([currentDesignMd], { type: "text/markdown;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  const languageSuffix = resultMode === "generated" && availableLanguages.length > 1 ? `-${activeLanguage}` : "";
  link.download = `${currentDomain.replace(/[^a-z0-9-]+/gi, "-").toLowerCase()}-DESIGN${languageSuffix}.md`;
  link.click();
  URL.revokeObjectURL(link.href);
});

downloadImagesButton.addEventListener("click", async () => {
  const assets = getSelectedVisualAssets();
  if (assets.length === 0) {
    return;
  }

  downloadImagesButton.disabled = true;
  const originalText = downloadImagesButton.textContent || "ZIP";
  downloadImagesButton.textContent = "Lade...";

  try {
    const response = await fetch("/api/download-images", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ assets }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({})) as { error?: string };
      throw new Error(errorData.error || "Die Bilddateien konnten nicht gepackt werden.");
    }

    const blob = await response.blob();
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${currentDomain.replace(/[^a-z0-9-]+/gi, "-").toLowerCase()}-visual-references.zip`;
    link.click();
    URL.revokeObjectURL(link.href);
  } catch (error) {
    renderWarnings([error instanceof Error ? error.message : "Die Bilddateien konnten nicht gepackt werden."]);
  } finally {
    downloadImagesButton.textContent = originalText;
    downloadImagesButton.disabled = getSelectedVisualAssets().length === 0;
  }
});

async function generate() {
  const urls = getSourceValues().map((url) => url.trim()).filter(Boolean);
  if (urls.length === 0) {
    renderWarnings(["Bitte gib mindestens eine URL ein."]);
    return;
  }

  setBusy(true);
  setStatus("Analysiere", "loading");
  clearWarnings();

  try {
    const response = await fetch("/api/generate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        urls,
        turnstileToken,
      }),
    });

    const data = (await response.json()) as GenerateResponse | { error?: string; code?: string };
    const isGenerateResponse = "designMd" in data;
    if (!response.ok || !isGenerateResponse) {
      const errorData = data as { error?: string; code?: string };
      throw new Error(errorData.error || "Die DESIGN.md-Datei konnte nicht erzeugt werden.");
    }

    renderResult(data);
    setStatus("Fertig", "ok");
  } catch (error) {
    const message = error instanceof Error ? error.message : "Unbekannter Fehler.";
    setStatus("Fehler", "error");
    renderWarnings([message]);
  } finally {
    setBusy(false);
    resetTurnstile();
  }
}

function renderResult(data: GenerateResponse) {
  resultMode = "generated";
  activeTemplateSlug = "";
  activeLanguage = "en";
  availableLanguages = data.designMdDe ? ["en", "de"] : ["en"];
  baseDesignMds = {
    en: data.designMd,
    ...(data.designMdDe ? { de: data.designMdDe } : {}),
  };
  currentDesignMds = { ...baseDesignMds };
  currentDesignMd = data.designMd;
  currentDomain = data.meta.domain;
  editableColors = buildEditableColors(data.meta.colors, data.meta.colorEvidence);
  editableFonts = [...data.meta.fonts];
  manualFonts.clear();
  editableFontEvidence = [...data.meta.fontEvidence];
  currentVisualAssets = [...data.meta.visualAssets];
  selectedVisualUrls = defaultVisualSelection(currentVisualAssets);

  outputText.readOnly = false;
  copyButton.disabled = false;
  downloadButton.disabled = false;
  downloadImagesButton.disabled = getSelectedVisualAssets().length === 0;
  addColorButton.disabled = false;
  addFontButton.disabled = false;
  brandEditor.disabled = false;

  brandEditor.value = data.meta.brandName;
  domainValue.textContent = data.meta.domain;
  renderColorEditor();
  renderFontEditor();
  renderVisuals(currentVisualAssets);
  renderLanguageTabs();
  renderTemplateRail();
  lintValue.textContent = `${data.meta.lintSummary.errors} Errors, ${data.meta.lintSummary.warnings} Warnings, ${data.meta.lintSummary.info} Infos`;
  refreshManualDesignMd();

  const visibleWarnings = data.meta.warnings.filter(isUserFacingWarning);
  if (visibleWarnings.length > 0) {
    renderWarnings(visibleWarnings);
  } else {
    clearWarnings();
  }
}

function renderTemplateRail() {
  templateRail.innerHTML = BRAND_TEMPLATES.map((template) => {
    const isActive = activeTemplateSlug === template.slug;
    const initials = template.name.split(/\s+/).map((part) => part[0]).join("").slice(0, 2).toUpperCase();
    return `
      <button class="template-card ${isActive ? "is-active" : ""}" type="button" data-template-slug="${escapeHtml(template.slug)}" title="${escapeHtml(template.description)}">
        <span class="template-card__logo">
          <img src="${escapeHtml(template.logoUrl)}" alt="" loading="lazy" />
          <span>${escapeHtml(initials)}</span>
        </span>
        <span class="template-card__name">${escapeHtml(template.name)}</span>
        <span class="template-card__meta">${escapeHtml(template.category)}</span>
      </button>
    `;
  }).join("");

  templateRail.querySelectorAll<HTMLImageElement>("img").forEach((image) => {
    image.addEventListener("error", () => {
      image.remove();
    }, { once: true });
  });
}

async function loadBrandTemplate(template: BrandTemplate) {
  setStatus("Vorlage", "loading");
  clearWarnings();

  try {
    const response = await fetch(template.templatePath);
    if (!response.ok) {
      throw new Error("Die Brand-Vorlage konnte nicht geladen werden.");
    }

    const sourceDesignMd = await response.text();
    const templateColors = extractTemplateColors(sourceDesignMd);
    const templateFonts = extractTemplateFonts(sourceDesignMd, template);
    const designMd = buildTemplateDesignMd(template, sourceDesignMd, templateColors, templateFonts);
    resultMode = "template";
    activeTemplateSlug = template.slug;
    activeLanguage = "en";
    availableLanguages = ["en"];
    baseDesignMds = { en: designMd };
    currentDesignMds = { en: designMd };
    currentDesignMd = designMd;
    currentDomain = template.slug;
    editableColors = buildTemplateEditableColors(templateColors);
    editableFonts = [...templateFonts];
    manualFonts.clear();
    editableFontEvidence = [];
    currentVisualAssets = [{
      url: template.logoUrl,
      type: "image",
      label: `${template.name} logo`,
      source: "Simple Icons",
      confidence: "medium",
    }];
    selectedVisualUrls = new Set<string>([template.logoUrl]);

    outputText.readOnly = false;
    outputText.value = designMd;
    copyButton.disabled = false;
    downloadButton.disabled = false;
    downloadImagesButton.disabled = getSelectedVisualAssets().length === 0;
    addColorButton.disabled = false;
    addFontButton.disabled = false;
    brandEditor.disabled = false;

    brandEditor.value = template.name;
    domainValue.innerHTML = `<a href="${escapeHtml(template.sourceUrl)}" target="_blank" rel="noreferrer">awesome-design-md / ${escapeHtml(template.slug)}</a>`;
    renderColorEditor();
    renderFontEditor();
    renderVisuals(currentVisualAssets);
    renderLanguageTabs();
    renderTemplateRail();
    lintValue.textContent = "Vorlage aus awesome-design-md";
    setStatus("Vorlage", "ok");
  } catch (error) {
    setStatus("Fehler", "error");
    renderWarnings([error instanceof Error ? error.message : "Die Brand-Vorlage konnte nicht geladen werden."]);
  }
}

function extractTemplateColors(sourceDesignMd: string) {
  const colors = Array.from(sourceDesignMd.matchAll(/#[0-9A-Fa-f]{6}\b/g))
    .map((match) => match[0].toUpperCase());
  return [...new Set(colors)].slice(0, 7);
}

function extractTemplateFonts(sourceDesignMd: string, template: BrandTemplate) {
  if (template.fonts && template.fonts.length > 0) {
    return template.fonts;
  }

  const candidates = Array.from(sourceDesignMd.matchAll(/fontFamily:\s*["']?([^"'\n]+)["']?/gi))
    .map((match) => match[1].split(",")[0].trim())
    .filter(Boolean);
  const proseFonts = Array.from(sourceDesignMd.matchAll(/\*\*([^*]{2,40})\*\*\s*(?:\(|:)/g))
    .map((match) => match[1].trim())
    .filter((value) => /font|sans|mono|serif|geist|inter|sohne|circular|gotham|sf pro|ibm plex|helvetica/i.test(value));
  const merged = [...new Set([...candidates, ...proseFonts])]
    .filter((font) => !/color|spacing|radius|shadow|button|card|primary|secondary/i.test(font))
    .slice(0, 2);
  return merged.length > 0 ? merged : ["Inter", "Geist"];
}

function buildTemplateEditableColors(colors: string[]): EditableColor[] {
  const values = colors.length > 0
    ? colors
    : Object.values(FALLBACK_COLORS).filter((value, index, list) => list.indexOf(value) === index);

  return COLOR_TOKEN_NAMES.map((role, index) => ({
    role,
    value: values[index] || FALLBACK_COLORS[role],
    evidence: {
      value: values[index] || FALLBACK_COLORS[role],
      source: "Brand-Vorlage",
      confidence: "medium",
      count: undefined,
      suggestedRole: role,
    },
  }));
}

function buildTemplateDesignMd(template: BrandTemplate, sourceDesignMd: string, colors: string[], fonts: string[]) {
  const editable = buildTemplateEditableColors(colors);
  const headingFont = fonts[0] || "Inter";
  const bodyFont = fonts[1] || fonts[0] || "Inter";
  const originalNotes = stripFrontmatter(sourceDesignMd).trim();

  return `---
version: "alpha"
name: ${yamlScalar(template.name)}
description: ${yamlScalar(`Curated DESIGN.md starter inspired by ${template.name}. Source: VoltAgent/awesome-design-md commit ${TEMPLATE_SOURCE_COMMIT.slice(0, 7)}.`)}
colors:
${buildColorLines(editable)}
typography:
  heading:
    fontFamily: ${yamlScalar(headingFont)}
    fontSize: 2.25rem
    fontWeight: 700
    lineHeight: 1.12
  body:
    fontFamily: ${yamlScalar(bodyFont)}
    fontSize: 1rem
    fontWeight: 400
    lineHeight: 1.5
  label:
    fontFamily: ${yamlScalar(bodyFont)}
    fontSize: 0.75rem
    fontWeight: 700
    lineHeight: 1.2
rounded:
  sm: 4px
  md: 8px
  full: 9999px
spacing:
  sm: 8px
  md: 16px
  lg: 24px
components:
  button-primary:
    backgroundColor: "{colors.accent}"
    textColor: "{colors.on-accent}"
    typography: "{typography.body}"
    rounded: "{rounded.md}"
    padding: 12px
  panel:
    backgroundColor: "{colors.surface}"
    textColor: "{colors.primary}"
    rounded: "{rounded.md}"
    padding: 16px
---

## Overview

Use this as a curated ${template.name}-inspired visual briefing for coding agents. It is based on public design notes from an older VoltAgent/awesome-design-md commit, not on an official brand manual.

## Colors

Use the extracted palette as a starting point, then remove or adjust colors that do not match the project. Keep the accent color for high-emphasis actions unless the source notes say otherwise.

## Typography

Use ${headingFont} for headings and ${bodyFont} for body text where available. If these fonts are not licensed or available in the project, choose a close system or open-source substitute.

## Layout

Follow the source brand's rhythm, density, and composition style, but adapt spacing to the product being built.

## Elevation & Depth

Prefer the source brand's observed depth language: flat borders for minimal systems, soft shadows for premium SaaS, and stronger contrast only when the notes support it.

## Shapes

Start with 8px radii for practical UI surfaces, then adjust toward sharper or rounder shapes if the source notes clearly show that pattern.

## Components

Apply the palette and typography to buttons, panels, navigation, cards, forms, and status elements. Keep components consistent rather than copying isolated decorative details.

## Do's and Don'ts

Do use this as an agent briefing and starting point. Don't treat it as official guidance, legal permission, or a complete brand system.

## Source Notes

Source: ${template.sourceUrl}

${originalNotes}`;
}

function stripFrontmatter(markdown: string) {
  return markdown.replace(/^---\s*\n[\s\S]*?\n---\s*\n?/, "");
}

function renderLanguageTabs() {
  languageTabs.hidden = resultMode !== "generated" || availableLanguages.length < 2;
  languageTabs.querySelectorAll<HTMLButtonElement>("[data-language]").forEach((button) => {
    const language = button.dataset.language as ResultLanguage;
    button.hidden = !availableLanguages.includes(language);
    button.classList.toggle("is-active", language === activeLanguage);
    button.setAttribute("aria-pressed", language === activeLanguage ? "true" : "false");
    button.textContent = LANGUAGE_LABELS[language] || language.toUpperCase();
  });
}

function setActiveLanguage(language: ResultLanguage) {
  activeLanguage = language;
  currentDesignMd = currentDesignMds[language] || "";
  outputText.value = currentDesignMd;
  renderLanguageTabs();
}

function renderSourceRows(values = getSourceValues()) {
  sourceList.innerHTML = sourceRows.map((rowId, index) => {
    const canRemove = sourceRows.length > 1;
    const value = values[index] ?? "";
    return `
      <div class="source-row">
        <input id="urlInput-${rowId}" class="input url-input" type="url" placeholder="${index === 0 ? "https://www.ainauten.com" : "https://news.ainauten.com"}" autocomplete="url" value="${escapeHtml(value)}" ${index === 0 ? "required" : ""} />
        <button class="icon-button source-remove" type="button" data-remove-source="${rowId}" aria-label="URL entfernen" title="URL entfernen" ${canRemove ? "" : "disabled"}>×</button>
      </div>
    `;
  }).join("");
  addUrlButton.disabled = sourceRows.length >= MAX_URLS;
}

function getSourceValues() {
  return Array.from(sourceList.querySelectorAll<HTMLInputElement>(".url-input")).map((input) => input.value);
}

function renderColorEditor() {
  colorEditor.innerHTML = editableColors.length
    ? editableColors.map((color, index) => `
      <div class="token-row token-row--color">
        <select class="token-select" data-token="color-role" data-index="${index}" aria-label="Token-Rolle ${index + 1}">
          ${COLOR_TOKEN_NAMES.map((name) => `<option value="${name}" ${name === color.role ? "selected" : ""}>${COLOR_TOKEN_LABELS[name]}</option>`).join("")}
        </select>
        <input class="color-picker" type="color" value="${toInputColor(color.value)}" data-token="color" data-index="${index}" aria-label="Farbe ${index + 1}" />
        <input class="input input--compact" type="text" value="${escapeHtml(color.value)}" data-token="color-text" data-index="${index}" aria-label="Hex-Farbe ${index + 1}" />
        <button class="mini-button" type="button" data-remove-token="color" data-index="${index}" aria-label="Farbe löschen">Löschen</button>
        <div class="token-meta">${color.manual ? '<span class="evidence-badge">Manuell angepasst</span>' : renderEvidenceBadge(color.evidence)}</div>
      </div>
    `).join("")
    : `<span class="empty-state">Keine Farben erkannt.</span>`;
}

function renderFontEditor() {
  fontEditor.innerHTML = editableFonts.length
    ? editableFonts.map((font, index) => `
      <div class="token-row token-row--font">
        <input class="input input--compact" type="text" value="${escapeHtml(font)}" data-token="font" data-index="${index}" aria-label="Font ${index + 1}" />
        <button class="mini-button" type="button" data-remove-token="font" data-index="${index}" aria-label="Font löschen">Löschen</button>
        <div class="token-meta">${manualFonts.has(index) ? '<span class="evidence-badge">Manuell angepasst</span>' : renderEvidenceBadge(editableFontEvidence.find((item) => item.value === font))}</div>
      </div>
    `).join("")
    : `<span class="empty-state">Keine Fonts erkannt.</span>`;
}

function renderVisuals(assets: VisualAsset[]) {
  visualValue.innerHTML = assets.length
    ? assets.map((asset) => `
      <button class="visual-card ${selectedVisualUrls.has(asset.url) ? "is-selected" : ""}" type="button" data-visual-url="${escapeHtml(asset.url)}" aria-pressed="${selectedVisualUrls.has(asset.url) ? "true" : "false"}">
        <span class="visual-thumb" data-kind="${escapeHtml(asset.type)}">
          <img src="${escapeHtml(asset.url)}" alt="" loading="lazy" />
        </span>
        <span>
          <strong>${escapeHtml(asset.label || asset.type)}</strong>
          <small>${escapeHtml([asset.type, asset.source, asset.confidence && confidenceLabel(asset.confidence)].filter(Boolean).join(" · "))}</small>
        </span>
        <span class="visual-check" aria-hidden="true">${selectedVisualUrls.has(asset.url) ? "✓" : "+"}</span>
      </button>
    `).join("")
    : "Keine Logos oder Visuals erkannt.";

  visualValue.querySelectorAll<HTMLImageElement>(".visual-thumb img").forEach((image) => {
    image.addEventListener("error", () => {
      const thumb = image.closest<HTMLElement>(".visual-thumb");
      thumb?.classList.add("visual-thumb--missing");
      image.remove();
    }, { once: true });
  });
  downloadImagesButton.disabled = getSelectedVisualAssets().length === 0;
}

function handleVisualSelection(event: Event) {
  const target = event.target as HTMLElement;
  const card = target.closest<HTMLButtonElement>("[data-visual-url]");
  if (!card) {
    return;
  }

  const url = card.dataset.visualUrl;
  if (!url) {
    return;
  }

  if (selectedVisualUrls.has(url)) {
    selectedVisualUrls.delete(url);
  } else {
    selectedVisualUrls.add(url);
  }

  renderVisuals(currentVisualAssets);
  refreshManualDesignMd();
}

function handleTokenInput(event: Event) {
  const input = event.target as HTMLInputElement | HTMLSelectElement;
  const index = Number(input.dataset.index);
  if (Number.isNaN(index)) {
    return;
  }
  const color = editableColors[index];

  if (input.dataset.token === "color-role") {
    if (!color) {
      return;
    }
    const role = COLOR_TOKEN_NAMES.includes(input.value as ColorRole) ? input.value as ColorRole : "accent";
    editableColors[index] = { ...color, role, manual: true };
  }

  if (input.dataset.token === "color") {
    if (!color) {
      return;
    }
    editableColors[index] = { ...color, value: input.value.trim().toUpperCase(), manual: true };
    renderColorEditor();
  }

  if (input.dataset.token === "color-text") {
    if (!color) {
      return;
    }
    editableColors[index] = { ...color, value: input.value.trim().toUpperCase(), manual: true };
  }

  if (input.dataset.token === "font") {
    manualFonts.add(index);
    editableFonts[index] = input.value.trim();
  }

  const badge = input.closest(".token-row")?.querySelector(".token-meta");
  if (badge) badge.innerHTML = '<span class="evidence-badge">Manuell angepasst</span>';
  refreshManualDesignMd();
}

function handleTokenRemove(event: Event) {
  const target = event.target as HTMLElement;
  const button = target.closest<HTMLButtonElement>("[data-remove-token]");
  if (!button) {
    return;
  }

  const index = Number(button.dataset.index);
  if (button.dataset.removeToken === "color") {
    editableColors.splice(index, 1);
    renderColorEditor();
  }
  if (button.dataset.removeToken === "font") {
    editableFonts.splice(index, 1);
    manualFonts = new Set([...manualFonts].filter(i => i !== index).map(i => i > index ? i - 1 : i));
    renderFontEditor();
  }

  refreshManualDesignMd();
}

function refreshManualDesignMd() {
  if (!baseDesignMds[activeLanguage]) {
    return;
  }

  for (const language of availableLanguages) {
    const base = baseDesignMds[language];
    if (base) {
      currentDesignMds[language] = applyManualEdits(base, language);
    }
  }

  currentDesignMd = currentDesignMds[activeLanguage] || "";
  outputText.value = currentDesignMd;
}

function applyManualEdits(designMd: string, language: ResultLanguage = activeLanguage) {
  if (resultMode !== "generated") {
    return applyVisualReferences(designMd, language);
  }
  return applyVisualReferences(synchronizeDesignDocument(designMd, {
    colors: editableColors, fonts: editableFonts,
    observedColors: editableColors.filter(color => color.evidence).map(color => color.evidence!.value),
    observedFonts: editableFontEvidence.map(item => item.value),
    manualFonts: [...manualFonts], name: brandEditor.value.trim() || "Brand",
  }, language), language);
}

function buildColorLines(colors: EditableColor[]) {
  const tokenMap = new Map(COLOR_TOKEN_NAMES.map(name => [name, FALLBACK_COLORS[name]]));
  for (const color of colors) {
    if (color.value && COLOR_TOKEN_NAMES.includes(color.role)) tokenMap.set(color.role, color.value);
  }
  return COLOR_TOKEN_NAMES.map(name => `  ${name}: "${tokenMap.get(name)}"`);
}

function applyVisualReferences(designMd: string, language: ResultLanguage = activeLanguage) {
  const cleanDesignMd = designMd.replace(/\n+## Visual References\n[\s\S]*$/m, "").trimEnd();
  const assets = getSelectedVisualAssets();
  if (assets.length === 0) {
    return cleanDesignMd;
  }

  const lines = assets.map((asset) => {
    const label = asset.label || asset.type;
    return `- ${label} (${asset.type}): ${asset.url}`;
  });

  const explainer = language === "de"
    ? "Nutze diese oeffentlichen Bild-URLs als visuelle Referenzen fuer Logo-Behandlung, Bildtonalitaet, Produkt-/Modulbilder und wiederkehrende visuelle Motive. Behandle sie nur als Referenz; verwende geschuetzte Bilder nicht direkt, wenn das Projekt keine Rechte daran hat."
    : "Use these public image URLs as visual references for logo treatment, image tone, product/module imagery, and recurring visual motifs. Treat them as references only; do not reuse protected imagery unless the project has the rights to do so.";

  return `${cleanDesignMd}

## Visual References

${explainer}

${lines.join("\n")}`;
}

function getSelectedVisualAssets() {
  return currentVisualAssets.filter((asset) => selectedVisualUrls.has(asset.url));
}

function defaultVisualSelection(assets: VisualAsset[]) {
  const selected = new Set<string>();
  for (const asset of assets) {
    const label = `${asset.label || ""} ${asset.url}`.toLowerCase();
    if (asset.type === "favicon" || asset.type === "og-image" || /logo|brand|mark|ainauten/.test(label)) {
      selected.add(asset.url);
    }
  }
  return selected;
}

async function loadConfig() {
  try {
    const response = await fetch("/api/config");
    if (!response.ok) {
      return;
    }
    const config = (await response.json()) as ConfigResponse;
    if (config.turnstileSiteKey) {
      setupTurnstile(config.turnstileSiteKey);
    }
  } catch {
    // The generator still works locally without Turnstile.
  }
}

function setupTurnstile(siteKey: string) {
  turnstileSlot.hidden = false;
  const script = document.createElement("script");
  script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  script.async = true;
  script.defer = true;
  script.onload = () => {
    if (!window.turnstile) {
      return;
    }
    turnstileWidgetId = window.turnstile.render(turnstileSlot, {
      sitekey: siteKey,
      callback: (token: string) => {
        turnstileToken = token;
      },
    });
  };
  document.head.appendChild(script);
}

function resetTurnstile() {
  turnstileToken = "";
  if (window.turnstile && turnstileWidgetId) {
    window.turnstile.reset(turnstileWidgetId);
  }
}

function setBusy(isBusy: boolean) {
  generateButton.disabled = isBusy;
  generateButton.textContent = isBusy ? "Generiert ..." : "DESIGN.md generieren";
}

function setStatus(label: string, state: "idle" | "loading" | "ok" | "error") {
  statusPill.textContent = label;
  statusPill.dataset.state = state;
}

function renderWarnings(warnings: string[]) {
  warningsBox.hidden = false;
  warningsBox.innerHTML = warnings.map((warning) => `<p>${escapeHtml(warning)}</p>`).join("");
}

function isUserFacingWarning(warning: string) {
  const hiddenFragments = [
    "Turnstile ist in dieser Umgebung nicht aktiv",
    "HTML wurde wegen der Größenbegrenzung gekürzt",
    "CSS wurde gekürzt",
    "Das KI-Ergebnis war nicht valide",
  ];

  return !hiddenFragments.some((fragment) => warning.includes(fragment));
}

function clearWarnings() {
  warningsBox.hidden = true;
  warningsBox.innerHTML = "";
}

function renderEvidenceBadge(evidence?: EvidenceItem) {
  if (!evidence) {
    return `<span class="evidence-badge evidence-badge--low">Vorgeschlagen</span>`;
  }

  const role = evidence.suggestedRole ? ` · ${COLOR_TOKEN_LABELS[evidence.suggestedRole]}` : "";
  const count = evidence.count && evidence.count > 1 ? ` · ${evidence.count}x` : "";
  return `<span class="evidence-badge evidence-badge--${evidence.confidence}">${confidenceLabel(evidence.confidence)} · ${escapeHtml(evidence.source)}${role}${count}</span>`;
}

function confidenceLabel(confidence: Confidence) {
  if (confidence === "high") {
    return "hoch";
  }
  if (confidence === "medium") {
    return "mittel";
  }
  return "niedrig";
}

function nextAvailableColorRole(offset = 0) {
  const used = new Set(editableColors.map((color) => color.role));
  return COLOR_TOKEN_NAMES.find((name, index) => index >= offset && !used.has(name))
    || COLOR_TOKEN_NAMES.find((name) => !used.has(name))
    || "accent";
}

function buildEditableColors(colors: string[], evidenceItems: EvidenceItem[]): EditableColor[] {
  const used = new Set<string>();
  return colors.map((value) => {
    const evidence = evidenceItems.find((item) => item.value === value);
    const preferred = evidence?.suggestedRole && COLOR_TOKEN_NAMES.includes(evidence.suggestedRole)
      ? evidence.suggestedRole
      : undefined;
    const role = preferred && !used.has(preferred)
      ? preferred
      : COLOR_TOKEN_NAMES.find((name) => !used.has(name)) || "accent";
    used.add(role);
    return { role, value, evidence };
  });
}

function initializeTheme() {
  const savedTheme = localStorage.getItem("theme");
  const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
  if (savedTheme === "dark" || (!savedTheme && prefersDark)) {
    document.documentElement.classList.add("dark");
  }
}

function toInputColor(value: string) {
  return /^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#7B2FF7";
}

function yamlScalar(value: string) {
  return JSON.stringify(value);
}

function query<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing element: ${selector}`);
  }
  return element;
}

function escapeHtml(value: string) {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}
